import { useEffect, useState, useMemo } from "react";
import { useOrder } from "@/context";
import { openModal, openEditModal, applyFieldsToTicket, loadFieldMapping, loadFieldMappingLabels } from "@/services";
import { SELECTED_ORDER_ID_KEY, ORDER_STORAGE_KEY, INTERACTIONS_STORAGE_KEY, TICKET_ID_KEY, JADLOG_CARRIER_KEY, TOTAL_EXPRESS_CARRIER_KEY, PATRUS_CARRIER_KEY, PATRUS_TOKEN_KEY } from "@/context";
import { getVtexDocument, getVtexOrder, getVtexOrderConversation, getVtextInteractions } from "@/services/vtex";
import { getCurrentTicket, zafClient, getCarrierFieldData, fillRequesterClassification } from "@/services/zendesk";
import type { VtexClientProfile } from "@/interfaces";
import type { VtexOrder } from "@/interfaces";
import { VTEX_PROPERTIES } from "@/constants";
import { APPLY_TRIGGER_KEY } from "@/pages/campos/useCampos";
import { classifyCustomer } from "@/utils/customerClassification";

function buildMappingEntries() {
  const labels = loadFieldMappingLabels()
  return Object.entries(labels).map(([, v]) => ({
    zendeskTitle: v.zendeskTitle,
    value: VTEX_PROPERTIES.find(p => p.key === v.vtexKey)?.path ?? v.vtexKey,
  }))
}

export function useHome() {
  const { orderList, isLoadingList, fetchOrderList, setOrder, setInteractions, conversation, setConversation, documents, setDocuments, order, setIsJadlogCarrier, setIsTotalExpressCarrier, setIsPatrusCarrier } = useOrder();
  const [clientProfile, setClientProfile] = useState<VtexClientProfile | null>(null);
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [currentQuery, setCurrentQuery] = useState("");
  const [mappingEntries, setMappingEntries] = useState(() => buildMappingEntries());
  const [ticketId, setTicketId] = useState<number | null>(null);
  const [showCpfConfirm, setShowCpfConfirm] = useState(false);
  const [classificationStatus, setClassificationStatus] = useState<'idle' | 'loading' | 'success' | 'downgrade_blocked'>('idle');
  const [searchLoading, setSearchLoading] = useState(false);

  // Computa a classificação do cliente baseada nos pedidos
  const customerTier = useMemo(() => {
    if (orderList.length > 0) {
      return classifyCustomer(orderList);
    }
    return null;
  }, [orderList]);

  // 1. Efeito para capturar o ID inicial ao carregar a Sidebar
  useEffect(() => {
    setOrder(null);
    async function fetchTicketContext() {
      try {
        const ticket = await getCurrentTicket();
        if (ticket && ticket.id) {
          setTicketId(ticket.id);
          localStorage.setItem(TICKET_ID_KEY, String(ticket.id));
          zafClient?.trigger('ticket_id_updated', ticket.id);
        }
      } catch (error) {
        console.error("Erro ao capturar ticket na Sidebar:", error);
      }
    }
    fetchTicketContext();
  }, []);

  // 2. Efeito para carregar dados do cliente quando a lista de pedidos mudar
  useEffect(() => {
    const updateProfile = async () => {
      if (orderList.length === 0) {
        setClientProfile(null);
        return;
      }

      try {
        setSearchLoading(true);
        const order = await getVtexOrder(orderList[0].orderId);
        const interactionsData = await getVtextInteractions(orderList[0].orderId);
        const conversationData = await getVtexOrderConversation(orderList[0].orderId);
        setOrder(order);
        setConversation(conversationData);
        setInteractions(interactionsData);
        setClientProfile(order.clientProfileData);

        try {
          const documentsDatas = await getVtexDocument(order.clientProfileData.document);
          setDocuments(documentsDatas);
        } catch (documentError) {
          console.error("Erro ao buscar documento VTEX:", documentError);
        }

        // detecta transportadora e faz fetch do token Patrus se necessário
        try {
          const vtexDeliveryCompany = order.shippingData?.logisticsInfo?.[0]?.deliveryCompany ?? ''
          const firstWord = vtexDeliveryCompany.trim().split(/\s+/)[0]?.toLowerCase() ?? ''

          let isJadlog = firstWord === 'jadlog'
          let isTotalExpress = firstWord === 'total'
          let isPatrus = firstWord === 'patrus'

          if (!isJadlog && !isTotalExpress && !isPatrus) {
            const carrierData = await getCarrierFieldData()
            const carrierValue = carrierData?.value?.toLowerCase() ?? ''
            isJadlog = carrierValue.includes('jadlog')
            isTotalExpress = carrierValue.includes('total_express')
            isPatrus = carrierValue.includes('patrus')
          }

          localStorage.setItem(JADLOG_CARRIER_KEY, String(isJadlog))
          localStorage.setItem(TOTAL_EXPRESS_CARRIER_KEY, String(isTotalExpress))
          localStorage.setItem(PATRUS_CARRIER_KEY, String(isPatrus))
          setIsJadlogCarrier(isJadlog)
          setIsTotalExpressCarrier(isTotalExpress)
          setIsPatrusCarrier(isPatrus)

          if (!isPatrus) {
            localStorage.removeItem(PATRUS_TOKEN_KEY)
          }
        } catch (carrierErr) {
          console.error('Erro ao detectar transportadora:', carrierErr)
        }

      } catch (error) {
        console.error("Erro ao buscar pedido VTEX:", error);
        setClientProfile(null);
      } finally {
        setSearchLoading(false);
      }
    };

    updateProfile();
  }, [orderList]);

const handleFillClassification = async () => {
  setClassificationStatus('loading');
  try {
    const requesterData = await zafClient?.get('ticket.requester') as {
      'ticket.requester': { id: number; email: string }
    };
    const requesterEmail = requesterData?.['ticket.requester']?.email?.toLowerCase() || '';
    const clientEmail = (
      (documents.map((doc) => doc.email).find((e) => !!e) || conversation[0]?.to[0].email || clientProfile?.email) || ''
    ).toLowerCase();

    if (requesterEmail && clientEmail && requesterEmail === clientEmail) {
      const requester = requesterData?.['ticket.requester'];
      if (!requester?.id || !customerTier) {
        console.warn('Dados insuficientes para preencher classificação');
        setClassificationStatus('idle');
        return;
      }
      const brand = order?.items?.[0]?.additionalInfo?.brandName ?? null;
      const result = await fillRequesterClassification(requester.id, customerTier.medal, brand);

      if (result?.updated) {
        setClassificationStatus('success');
      } else if (result?.reason === 'downgrade_blocked') {
        setClassificationStatus('downgrade_blocked');
      } else {
        setClassificationStatus('idle');
      }
    } else {
      setShowCpfConfirm(true);
      setClassificationStatus('idle');
    }
  } catch {
    setClassificationStatus('idle');
  }
};

const handleConfirmCpfFill = async () => {
  setClassificationStatus('loading');
  try {
    const requesterData = await zafClient?.get('ticket.requester') as {
      'ticket.requester': { id: number; email: string }
    };
    const requester = requesterData?.['ticket.requester'];
    if (requester?.id && customerTier) {
      const brand = order?.items?.[0]?.additionalInfo?.brandName ?? null;
      const result = await fillRequesterClassification(requester.id, customerTier.medal, brand);

      if (result?.updated) {
        setClassificationStatus('success');
      } else if (result?.reason === 'downgrade_blocked') {
        setClassificationStatus('downgrade_blocked');
      } else {
        setClassificationStatus('idle');
      }
    }
  } catch {
    setClassificationStatus('idle');
  }
  setShowCpfConfirm(false);
};

  const handleCloseCpfConfirm = () => setShowCpfConfirm(false);

 async function handleSearch(q: string) {
   setCurrentQuery(q);
   if (q.trim()) {
     setOrder(null);
     setInteractions([]);
     setClientProfile(null);
     setConversation([]);
     localStorage.removeItem(ORDER_STORAGE_KEY);
     localStorage.removeItem(INTERACTIONS_STORAGE_KEY);
     setSearchLoading(true);
     try {
       await fetchOrderList(q.trim(), showIncomplete);
     } finally {
       setSearchLoading(false);
     }
   }
 }

  function handleToggleIncomplete(value: boolean) {
    setShowIncomplete(value);
    if (currentQuery.trim()) fetchOrderList(currentQuery.trim(), value);
  }

  async function handleOpenModal(orderId: string, order?: VtexOrder) {
    localStorage.setItem(SELECTED_ORDER_ID_KEY, orderId);
    localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(order));

    if (ticketId) {
      localStorage.setItem('CURRENT_TICKET_ID', String(ticketId));
    }

    openModal(ticketId);
  }

  function handleOpenEditModal() {
    openEditModal();
  }

  // Atualiza o accordion quando o modal de EditarCampos salva o mapping
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === 'conciex:field-mapping-labels') {
        setMappingEntries(buildMappingEntries())
        return
      }

      if (e.key === APPLY_TRIGGER_KEY) {
        try {
          const stored = localStorage.getItem(ORDER_STORAGE_KEY)
          if (!stored) return
          const order = JSON.parse(stored) as VtexOrder
          const mapping = loadFieldMapping()
          applyFieldsToTicket(order, mapping)
        } catch {
          // silent
        }
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  useEffect(() => {
    if (classificationStatus === 'success') {
      const timer = setTimeout(() => setClassificationStatus('idle'), 3000)
      return () => clearTimeout(timer)
    }
  }, [classificationStatus])

  async function handleApplyData() {
    try {
      const stored = localStorage.getItem(ORDER_STORAGE_KEY)
      if (!stored) return
      const currentOrder = JSON.parse(stored) as VtexOrder
      const mapping = loadFieldMapping()
      await applyFieldsToTicket(currentOrder, mapping)
    } catch {
      // silent
    }
  }

   return {
     orderList,
     isLoadingList,
     clientProfile,
     ticketId,
     handleSearch,
     handleOpenModal,
     handleOpenEditModal,
     handleApplyData,
     mappingEntries,
     showIncomplete,
     handleToggleIncomplete,
     conversation,
     documents,
     order,
     customerTier,
     showCpfConfirm,
     classificationStatus,
     handleConfirmCpfFill,
     handleCloseCpfConfirm,
     handleFillClassification,
     searchLoading,
   };
}
